import { isDefined } from "@clipboard-health/util-ts";

import { type BasePaginatedResponse, type RequestPagination } from "../lib/api/types";

export interface PaginatedQueryParameters {
  page?: RequestPagination;
}

export interface PaginationPageParameters {
  nextPageCursor?: string;
}

interface GetPaginationNextPageContextProps {
  pageParam: PaginationPageParameters;
  queryParams: PaginatedQueryParameters;
}

/**
 * This is an implementation of the react-query `getNextPageParam` function.
 * It uses our default convention for pagination, which is to use a cursor.
 * The cursor is a string that is returned in the `links` object of the response.
 * It is not returned in the response if there are no more results.
 */
export function getPaginationNextPageParameters(lastPage: BasePaginatedResponse) {
  const nextPageCursor = lastPage?.links?.nextCursor;

  if (isDefined(nextPageCursor)) {
    return { nextPageCursor };
  }

  return undefined;
}

/**
 * This function is used to transform the pageParams for the next page queryParams.
 * Specifically, it adds the nextPageCursor from pageParam as the `cursor` on the `queryParams.page` object.
 * Resulting queryParams are merged with the existing queryParams and passed to the next request
 */
export async function getPaginationNextPageContext({
  pageParam,
  queryParams,
}: GetPaginationNextPageContextProps): Promise<{
  queryParams?: Record<string, any> | undefined;
}> {
  const { nextPageCursor } = pageParam;

  if (isDefined(nextPageCursor)) {
    const newQueryParams = {
      ...queryParams,
      page: {
        ...queryParams.page,
        cursor: nextPageCursor,
      },
    };

    return { queryParams: newQueryParams };
  }

  return { queryParams: undefined };
}
